import { type FC, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import CaseDeadlinesCentral from '@/components/feature/cases/CaseDeadlinesCentral';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { EmptyState, FilterBar, LoadingState, SearchInput } from '@/components/base';
import { type CreateLegalDeadlineInput, type LegalDeadline, useLegalDeadlines } from '@/hooks/useLegalDeadlines';

type DeadlineFormState = {
  name: string;
  legalArea: string;
  deadlineType: string;
  daysCount: string;
  countType: 'business_days' | 'calendar_days' | 'variable';
  legalBasis: string;
  startTerm: string;
  endTerm: string;
  practicalNotes: string;
  riskLevel: 'low' | 'medium' | 'high';
  tags: string;
  isPinned: boolean;
};

const countTypeLabels: Record<DeadlineFormState['countType'], string> = {
  business_days: 'Dias úteis',
  calendar_days: 'Dias corridos',
  variable: 'Variável',
};

const riskLevelLabels: Record<DeadlineFormState['riskLevel'], string> = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
};

const emptyFormState: DeadlineFormState = {
  name: '',
  legalArea: '',
  deadlineType: '',
  daysCount: '',
  countType: 'business_days',
  legalBasis: '',
  startTerm: '',
  endTerm: '',
  practicalNotes: '',
  riskLevel: 'medium',
  tags: '',
  isPinned: false,
};

const deadlineToFormState = (deadline: LegalDeadline): DeadlineFormState => ({
  name: deadline.name,
  legalArea: deadline.legalArea,
  deadlineType: deadline.deadlineType ?? '',
  daysCount: deadline.daysCount === null || deadline.daysCount === undefined ? '' : String(deadline.daysCount),
  countType: deadline.countType,
  legalBasis: deadline.legalBasis ?? '',
  startTerm: deadline.startTerm ?? '',
  endTerm: deadline.endTerm ?? '',
  practicalNotes: deadline.practicalNotes ?? '',
  riskLevel: deadline.riskLevel,
  tags: deadline.tags,
  isPinned: deadline.isPinned,
});

const normalizeText = (value: string) => value.trim();

const buildPayload = (form: DeadlineFormState): CreateLegalDeadlineInput => ({
  name: normalizeText(form.name),
  legalArea: normalizeText(form.legalArea),
  deadlineType: normalizeText(form.deadlineType) || null,
  daysCount:
    form.countType === 'variable'
      ? null
      : form.daysCount.trim() === ''
      ? null
      : Number.isFinite(Number(form.daysCount)) && Number(form.daysCount) >= 0
      ? Number(form.daysCount)
      : null,
  countType: form.countType,
  legalBasis: normalizeText(form.legalBasis) || null,
  startTerm: normalizeText(form.startTerm) || null,
  endTerm: normalizeText(form.endTerm) || null,
  practicalNotes: normalizeText(form.practicalNotes) || null,
  riskLevel: form.riskLevel,
  tags: normalizeText(form.tags) || null,
  isPinned: form.isPinned,
  isActive: true,
});

const formatDeadlineCount = (deadline: LegalDeadline) => {
  if (deadline.countType === 'variable') {
    return 'Variável';
  }

  if (deadline.daysCount === null || deadline.daysCount === undefined) {
    return countTypeLabels[deadline.countType];
  }

  return `${deadline.daysCount} ${deadline.countType === 'business_days' ? 'dias úteis' : 'dias corridos'}`;
};

const formatTags = (tags: string) =>
  tags
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

const DeadlinesPage: FC = () => {
  const { canManageLegalStructures } = useRolePermissions();
  const {
    legalDeadlines,
    isLoadingLegalDeadlines,
    isSavingLegalDeadlines,
    legalDeadlinesError,
    legalDeadlinesSuccess,
    loadLegalDeadlines,
    createLegalDeadline,
    updateLegalDeadline,
    archiveLegalDeadline,
    clearLegalDeadlinesFeedback,
  } = useLegalDeadlines();
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [countTypeFilter, setCountTypeFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [editingDeadlineId, setEditingDeadlineId] = useState<string | null>(null);
  const [form, setForm] = useState<DeadlineFormState>(emptyFormState);
  const [localMessage, setLocalMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const areaOptions = useMemo(
    () => Array.from(new Set(legalDeadlines.map((deadline) => deadline.legalArea).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, 'pt-BR')
    ),
    [legalDeadlines]
  );

  const filteredDeadlines = useMemo(() => {
    const term = search.trim().toLowerCase();

    return legalDeadlines.filter((deadline) => {
      const matchesArea = !areaFilter || deadline.legalArea === areaFilter;
      const matchesCountType = !countTypeFilter || deadline.countType === countTypeFilter;
      const matchesRisk = !riskFilter || deadline.riskLevel === riskFilter;
      const matchesSearch =
        !term ||
        [
          deadline.name,
          deadline.legalArea,
          deadline.deadlineType,
          deadline.legalBasis,
          deadline.startTerm,
          deadline.endTerm,
          deadline.practicalNotes,
          deadline.tags,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));

      return matchesArea && matchesCountType && matchesRisk && matchesSearch;
    });
  }, [areaFilter, countTypeFilter, legalDeadlines, riskFilter, search]);

  const openCreateForm = () => {
    setEditingDeadlineId(null);
    setForm(emptyFormState);
    clearLegalDeadlinesFeedback();
    setLocalMessage(null);
  };

  const openEditForm = (deadline: LegalDeadline) => {
    setEditingDeadlineId(deadline.id);
    setForm(deadlineToFormState(deadline));
    clearLegalDeadlinesFeedback();
    setLocalMessage(null);
  };

  const clearFilters = () => {
    setSearch('');
    setAreaFilter('');
    setCountTypeFilter('');
    setRiskFilter('');
  };

  const handleFormChange = (field: keyof DeadlineFormState, value: string | boolean) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    const payload = buildPayload(form);

    if (!payload.name) {
      setLocalMessage({ kind: 'error', text: 'O nome do prazo é obrigatório.' });
      return;
    }

    if (!payload.legalArea) {
      setLocalMessage({ kind: 'error', text: 'A área jurídica é obrigatória.' });
      return;
    }

    setLocalMessage(null);

    try {
      if (editingDeadlineId) {
        await updateLegalDeadline(editingDeadlineId, payload);
        setLocalMessage({ kind: 'success', text: 'Prazo jurídico atualizado com sucesso.' });
      } else {
        await createLegalDeadline(payload);
        setLocalMessage({ kind: 'success', text: 'Prazo jurídico criado com sucesso.' });
      }

      setEditingDeadlineId(null);
      setForm(emptyFormState);
      await loadLegalDeadlines();
    } catch (error: unknown) {
      setLocalMessage({
        kind: 'error',
        text: error instanceof Error ? error.message : 'Não foi possível salvar o prazo jurídico.',
      });
    }
  };

  const handleArchive = async (deadline: LegalDeadline) => {
    if (!window.confirm('Tem certeza que deseja arquivar este prazo jurídico? Esta ação removerá o prazo da listagem, mas manterá o registro no banco para auditoria.')) {
      return;
    }

    try {
      await archiveLegalDeadline(deadline.id);
      if (editingDeadlineId === deadline.id) {
        setEditingDeadlineId(null);
        setForm(emptyFormState);
      }
      setLocalMessage({ kind: 'success', text: 'Prazo jurídico arquivado com sucesso.' });
    } catch (error: unknown) {
      setLocalMessage({
        kind: 'error',
        text: error instanceof Error ? error.message : 'Não foi possível arquivar o prazo jurídico.',
      });
    }
  };

  const handleTogglePin = async (deadline: LegalDeadline) => {
    try {
      await updateLegalDeadline(deadline.id, {
        ...deadlineToFormState(deadline),
        daysCount: deadline.daysCount,
        isPinned: !deadline.isPinned,
        isActive: deadline.isActive,
      });
      setLocalMessage({
        kind: 'success',
        text: deadline.isPinned ? 'Prazo desfixado com sucesso.' : 'Prazo fixado com sucesso.',
      });
    } catch (error: unknown) {
      setLocalMessage({
        kind: 'error',
        text: error instanceof Error ? error.message : 'Não foi possível alterar a fixação do prazo.',
      });
    }
  };

  const showEmptyState = !isLoadingLegalDeadlines && filteredDeadlines.length === 0;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">

        {!canManageLegalStructures ? (
          <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
            Modo consulta: seu perfil pode visualizar este módulo, mas alterações estruturais são reservadas a administradores e advogados.
          </div>
        ) : null}
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <h2 className="text-xl font-bold text-foreground-900">Prazos Jurídicos</h2>
            <p className="text-sm text-foreground-500">
              Central interna para cadastrar, organizar e personalizar os prazos utilizados no escritório.
            </p>
          </div>

          <button
            type="button"
            onClick={() => void loadLegalDeadlines()}
            className="inline-flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-refresh-line" />
            Atualizar
          </button>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Central de consulta interna. Os prazos devem ser conferidos conforme legislação vigente, ato de intimação,
          sistema processual, feriados, suspensão de expediente, regimento interno e peculiaridades do caso concreto.
        </div>

        <CaseDeadlinesCentral />

        <div className="flex items-center gap-2 pt-1">
          <div className="h-px flex-1 bg-background-200"></div>

          <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400">
            Biblioteca de regras jurídicas
          </span>

          <div className="h-px flex-1 bg-background-200"></div>
        </div>

        {legalDeadlinesError || legalDeadlinesSuccess || localMessage ? (
          <div
            className={`rounded-xl px-4 py-3 text-sm ${
              (localMessage?.kind === 'error' || legalDeadlinesError)
                ? 'border border-amber-200 bg-amber-50 text-amber-800'
                : 'border border-emerald-200 bg-emerald-50 text-emerald-800'
            }`}
          >
            {localMessage?.text || legalDeadlinesError || legalDeadlinesSuccess}
          </div>
        ) : null}

        <section hidden={!canManageLegalStructures} className="rounded-2xl border border-background-200 bg-background-50 shadow-sm">
          <div className="flex flex-col gap-3 border-b border-background-100 px-4 py-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-foreground-800">
                {editingDeadlineId ? 'Editar prazo' : 'Novo prazo jurídico'}
              </h3>
              <p className="text-xs text-foreground-500">
                Cadastre seus próprios prazos conforme a atuação e o fluxo do escritório.
              </p>
            </div>
            {editingDeadlineId ? (
              <button
                type="button"
                onClick={() => {
                  setEditingDeadlineId(null);
                  setForm(emptyFormState);
                  clearLegalDeadlinesFeedback();
                }}
                className="inline-flex items-center gap-2 rounded-lg border border-background-200 px-3 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100"
              >
                Limpar edição
              </button>
            ) : null}
          </div>

          <div className="p-4 space-y-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Nome *</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(event) => handleFormChange('name', event.target.value)}
                  placeholder="Ex.: Contestação cível"
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                />
              </label>

              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Área jurídica *</span>
                <input
                  type="text"
                  value={form.legalArea}
                  onChange={(event) => handleFormChange('legalArea', event.target.value)}
                  placeholder="Ex.: Cível"
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                />
              </label>

              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Tipo de prazo</span>
                <input
                  type="text"
                  value={form.deadlineType}
                  onChange={(event) => handleFormChange('deadlineType', event.target.value)}
                  placeholder="Ex.: Recurso, defesa, pedido"
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                />
              </label>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Contagem *</span>
                <select
                  value={form.countType}
                  onChange={(event) => handleFormChange('countType', event.target.value as DeadlineFormState['countType'])}
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                >
                  {Object.entries(countTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Quantidade de dias</span>
                <input
                  type="number"
                  min="0"
                  value={form.daysCount}
                  onChange={(event) => handleFormChange('daysCount', event.target.value)}
                  placeholder="Opcional quando variável"
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                />
              </label>

              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Nível de risco *</span>
                <select
                  value={form.riskLevel}
                  onChange={(event) => handleFormChange('riskLevel', event.target.value as DeadlineFormState['riskLevel'])}
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                >
                  {Object.entries(riskLevelLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Fixado</span>
                <button
                  type="button"
                  onClick={() => handleFormChange('isPinned', !form.isPinned)}
                  className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    form.isPinned
                      ? 'border-primary-200 bg-primary-50 text-primary-700'
                      : 'border-background-200 bg-background-50 text-foreground-700'
                  }`}
                >
                  <span>{form.isPinned ? 'Sim' : 'Não'}</span>
                  <i className={form.isPinned ? 'ri-star-fill' : 'ri-star-line'} />
                </button>
              </label>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Base legal</span>
                <textarea
                  value={form.legalBasis}
                  onChange={(event) => handleFormChange('legalBasis', event.target.value)}
                  rows={2}
                  placeholder="Ex.: CPC, art. 335"
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                />
              </label>

              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Termo inicial</span>
                <textarea
                  value={form.startTerm}
                  onChange={(event) => handleFormChange('startTerm', event.target.value)}
                  rows={2}
                  placeholder="Ex.: Ciência da intimação"
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                />
              </label>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Termo final / observação de término</span>
                <textarea
                  value={form.endTerm}
                  onChange={(event) => handleFormChange('endTerm', event.target.value)}
                  rows={2}
                  placeholder="Ex.: Antes do início da audiência"
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                />
              </label>

              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Observações práticas</span>
                <textarea
                  value={form.practicalNotes}
                  onChange={(event) => handleFormChange('practicalNotes', event.target.value)}
                  rows={2}
                  placeholder="Ex.: Conferir intimação, prazo local e peculiaridades do caso."
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                />
              </label>
            </div>

            <div className="grid gap-3 md:grid-cols-[1fr_180px]">
              <label className="space-y-1">
                <span className="block text-xs font-medium text-foreground-600">Tags</span>
                <input
                  type="text"
                  value={form.tags}
                  onChange={(event) => handleFormChange('tags', event.target.value)}
                  placeholder="Ex.: cpc, recurso, audiência"
                  className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-300 focus:outline-none"
                />
              </label>

              <div className="flex items-end gap-2">
                <button
            hidden={!canManageLegalStructures}
                  type="button"
                  onClick={handleSave}
                  disabled={isSavingLegalDeadlines}
                  className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-primary-500 px-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSavingLegalDeadlines ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                  {editingDeadlineId ? 'Salvar alterações' : 'Criar prazo'}
                </button>
              </div>
            </div>
          </div>
        </section>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nome, área, tipo, base legal, termo inicial, observações ou tags..."
            className="w-full lg:max-w-xl"
          />

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-100"
            >
              Limpar filtros
            </button>
          </div>
        </div>

        <FilterBar
          filters={[
            {
              key: 'area',
              label: 'Área',
              value: areaFilter,
              onChange: setAreaFilter,
              options: areaOptions.map((area) => ({ label: area, value: area })),
            },
            {
              key: 'countType',
              label: 'Contagem',
              value: countTypeFilter,
              onChange: setCountTypeFilter,
              options: Object.entries(countTypeLabels).map(([value, label]) => ({ label, value })),
            },
            {
              key: 'riskLevel',
              label: 'Risco',
              value: riskFilter,
              onChange: setRiskFilter,
              options: Object.entries(riskLevelLabels).map(([value, label]) => ({ label, value })),
            },
          ]}
        />

        {isLoadingLegalDeadlines ? (
          <LoadingState message="Carregando prazos jurídicos..." />
        ) : showEmptyState ? (
          <EmptyState
            icon="ri-time-line"
            title="Nenhum prazo cadastrado"
            description="Cadastre seus próprios prazos jurídicos para manter esta central personalizada e útil ao fluxo do escritório."
            action={canManageLegalStructures ? { label: 'Cadastrar prazo', onClick: openCreateForm, icon: 'ri-add-line' } : undefined}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {filteredDeadlines.map((deadline) => {
              const isHighRisk = deadline.riskLevel === 'high';

              return (
                <article
                  key={deadline.id}
                  className={`rounded-2xl border p-4 shadow-sm ${
                    isHighRisk ? 'border-red-200 bg-red-50' : 'border-background-200 bg-background-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground-900">{deadline.name}</h3>
                        {deadline.isPinned ? (
                          <span className="rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700">
                            Fixado
                          </span>
                        ) : null}
                      </div>
                      <p className="text-xs text-foreground-500">
                        {deadline.legalArea} · {deadline.deadlineType || 'Tipo não informado'}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
            hidden={!canManageLegalStructures}
                        type="button"
                        onClick={() => void handleTogglePin(deadline)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-background-200 text-foreground-600 transition-colors hover:bg-background-100"
                        title={deadline.isPinned ? 'Desfixar' : 'Fixar'}
                      >
                        <i className={deadline.isPinned ? 'ri-star-fill text-primary-600' : 'ri-star-line'} />
                      </button>
                      <button
            hidden={!canManageLegalStructures}
                        type="button"
                        onClick={() => openEditForm(deadline)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-background-200 text-foreground-600 transition-colors hover:bg-background-100"
                        title="Editar"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
            hidden={!canManageLegalStructures}
                        type="button"
                        onClick={() => void handleArchive(deadline)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-background-200 text-foreground-600 transition-colors hover:bg-background-100"
                        title="Arquivar"
                      >
                        <i className="ri-archive-line" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 text-sm md:grid-cols-2">
                    <div>
                      <p className="text-[11px] text-foreground-500">Prazo / contagem</p>
                      <p className="font-medium text-foreground-800">{formatDeadlineCount(deadline)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-foreground-500">Risco</p>
                      <p
                        className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                          deadline.riskLevel === 'high'
                            ? 'border-red-200 bg-red-100 text-red-700'
                            : deadline.riskLevel === 'medium'
                            ? 'border-amber-200 bg-amber-100 text-amber-700'
                            : 'border-emerald-200 bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        {riskLevelLabels[deadline.riskLevel]}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-foreground-500">Base legal</p>
                      <p className="font-medium text-foreground-800">{deadline.legalBasis || 'Sem base legal informada'}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-foreground-500">Termo inicial</p>
                      <p className="font-medium text-foreground-800">{deadline.startTerm || 'Não informado'}</p>
                    </div>
                  </div>

                  {deadline.endTerm ? (
                    <div className="mt-3">
                      <p className="text-[11px] text-foreground-500">Termo final / observação</p>
                      <p className="text-sm text-foreground-700">{deadline.endTerm}</p>
                    </div>
                  ) : null}

                  {deadline.practicalNotes ? (
                    <div className="mt-3">
                      <p className="text-[11px] text-foreground-500">Observações práticas</p>
                      <p className="text-sm text-foreground-700">{deadline.practicalNotes}</p>
                    </div>
                  ) : null}

                  <div className="mt-3 flex flex-wrap gap-2">
                    {formatTags(deadline.tags).length > 0 ? (
                      formatTags(deadline.tags).map((tag) => (
                        <span
                          key={`${deadline.id}-${tag}`}
                          className="rounded-full border border-background-200 bg-background-100 px-2 py-0.5 text-[10px] text-foreground-600"
                        >
                          #{tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-foreground-500">Sem tags</span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!isLoadingLegalDeadlines && filteredDeadlines.length === 0 && legalDeadlines.length > 0 ? (
          <div className="rounded-xl border border-background-200 bg-background-50 p-5 text-sm text-foreground-600">
            Nenhum prazo encontrado com os filtros atuais.
          </div>
        ) : null}
      </div>
    </AppLayout>
  );
};

export default DeadlinesPage;
